import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Grid, Divider, Box, Button } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import clsx from "clsx";
import SearchService from "../../services/SearchService";
import apiClient from "../../auth/apiClient";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useSelector } from "react-redux";

import OneParkInviteSearchButton from "./OneParkInviteSearchButton";
import OneParkInviteTable from "./OneParkInviteTable";
import { useStyles } from "./styles";

const SEARCH_LIMIT = 1000;
const ITEM_LIMIT = 10;

const searchService = new SearchService(apiClient);

const formatLastUse = (value) => {
  if (!value) return "--";
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "--";
  }
};

const descendingComparator = (a, b, orderBy) => {
  if (!b[orderBy]) return -1;
  if (!a[orderBy]) return 1;
  if (orderBy === "lastUse") {
    return new Date(b.lastUseRaw) - new Date(a.lastUseRaw);
  }

  return b[orderBy]
    .toString()
    .toLowerCase()
    .localeCompare(a[orderBy].toString().toLowerCase());
};

const getComparator = (order, orderBy) =>
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);

const OneParkInviteContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();

  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID
  );

  const [activeSearch, setActiveSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [orderBy, setOrderBy] = useState("firstName");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);

  const fetchInviteResults = useCallback(async () => {
    if (!activeSearch) return;

    setLoading(true);

    try {
      const response = await searchService.searchAccessHolders(
        scopeAwareFacilityID,
        activeSearch.searchTerm,
        activeSearch.searchCategory,
        null,
        null,
        null,
        null,
        SEARCH_LIMIT,
        0
      );

      const formatted =
        response?.data?.collection?.map((r) => ({
          id: r.accessHolderID,
          firstName: r.contactInfo?.firstName ?? "",
          lastName: r.contactInfo?.lastName ?? "",
          email:
            r.contactInfo?.emails?.length > 0
              ? r.contactInfo.emails[0].emailAddress
              : "",
          lastUse: formatLastUse(r.lastUse),
          lastUseRaw: r.lastUse,
        })) ?? [];

      setRows(formatted);
      setPage(1);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeSearch, scopeAwareFacilityID]);

  useEffect(() => {
    fetchInviteResults();
  }, [fetchInviteResults]);

  const sortedRows = useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * ITEM_LIMIT;
    return sortedRows.slice(start, start + ITEM_LIMIT);
  }, [sortedRows, page]);

  return (
    <Box className={clsx("pageable-entity", "access-holder-pageable-entity", "onepark-invite-container")}>
      <Grid container>
        <Grid item xs={12} md={9}>
          <OneParkInviteSearchButton
            loading={loading}
            onSearch={setActiveSearch}
          />
          <Divider className={clsx(classes.sectionDivider, "onepark-invite-divider")} />
        </Grid>

        <Grid item xs={12} md={3}>
          {activeSearch && sortedRows.length > ITEM_LIMIT && (
            <div className={clsx(classes.paginationWrapper, "onepark-invite-pagination-wrapper")}>
              <Pagination
                className={clsx("onepark-invite-pagination")}
                page={page}
                count={Math.ceil(sortedRows.length / ITEM_LIMIT)}
                onChange={(e, value) => setPage(value)}
                size="small"
                shape="rounded"
                color="primary"
              />
            </div>
          )}
        </Grid>
      </Grid>

      {activeSearch && (
        <div className={clsx(classes.contentWrapper, "onepark-invite-content-wrapper")}>
          <div className={clsx(classes.contentInner, "onepark-invite-content-inner")}>
            {paginatedRows.length > 0 ? (
              <>
                <OneParkInviteTable
                  rows={paginatedRows}
                  order={order}
                  orderBy={orderBy}
                  onSort={(column) => {
                    const isAsc = orderBy === column && order === "asc";
                    setOrder(isAsc ? "desc" : "asc");
                    setOrderBy(column);
                  }}
                />
                <div className={clsx(classes.sendInvitesWrapper, "onepark-send-invites-wrapper")}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={clsx("onepark-send-invites-button")}
                  >
                    Send Invites To List
                  </Button>
                </div>
              </>
            ) : (
              !loading && (
                <div className={clsx(classes.noResults, "onepark-invite-no-results")}>
                  <span className={clsx("no-results-text", "onepark-invite-no-results-text")}>No results found</span>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </Box>
  );
};

export default OneParkInviteContainer;
